import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import FinanceNav from "@/components/finance/FinanceNav";
import { PageHeader } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/lib/roles";

export const paymentStatusLabel = (status) => ({
  paid: "Paid",
  fully_sponsored: "Fully Sponsored",
  partially_sponsored: "Partially Sponsored",
  paid_plus_scholarship: "Paid + Scholarship",
  fully_discounted: "Fully Discounted",
  partially_discounted: "Partially Discounted",
  partially_paid: "Partial",
  unpaid: "Unpaid",
  pending_verification: "Pending Verification",
  not_applicable: "Not Applicable",
  awaiting_issue: "Paid — Awaiting Issue",
  issued: "Issued",
  reversed: "Reversed",
  not_issuable: "Not Issuable",
}[status] || status);

export const paymentMoney = (minor) =>
  `₦${((Number(minor) || 0) / 100).toLocaleString()}`;

export const flattenOperationalRows = (rows = []) => rows.flatMap((student) =>
  (student.items || []).map((item) => ({ ...item, student_id: student.student_id, enrollment_id: student.enrollment_id,
    student_name: student.student_name, admission_no: student.admission_no, class_id: student.class_id,
    class_name: student.class_name, arm_id: student.arm_id, arm_name: student.arm_name })),
);

export const paymentOperationsCsv = (rows) => {
  const columns = ["Student", "Admission Number", "Class", "Arm", "Item", "Type", "Quantity", "Charge", "Scholarship", "Discount", "Cash Paid", "Balance", "Payment Status", "Issue Status"];
  const quote = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  return [columns, ...rows.map((row) => [row.student_name, row.admission_no, row.class_name, row.arm_name || "", row.name,
    row.item_type === "physical" ? "Physical Item" : "Service", row.quantity || 1, (row.required_minor || 0) / 100,
    (row.scholarship_minor || 0) / 100, (row.discount_minor || 0) / 100, (row.paid_minor || 0) / 100, (row.outstanding_minor || 0) / 100, paymentStatusLabel(row.status),
    row.item_type === "physical" ? paymentStatusLabel(row.item_status) : "Not Applicable"])].map((line) => line.map(quote).join(",")).join("\n");
};

export const canIssuePhysicalItems = (user) => {
  const roles = user?.roles?.length ? user.roles : user?.role ? [user.role] : [];
  return roles.some((role) => ["super_admin", "director", "inventory_officer"].includes(role));
};


const ISSUE_FILTERS = [["all", "All issue states"], ["awaiting_issue", "Awaiting Issue"], ["issued", "Issued"], ["reversed", "Reversed"], ["not_issuable", "Not Issuable"]];
const STATUS_FILTERS = [
  ["all", "All"],
  ["paid", "Paid"],
  ["fully_sponsored", "Fully Sponsored"],
  ["partially_sponsored", "Partially Sponsored"],
  ["paid_plus_scholarship", "Paid + Scholarship"],
  ["partially_paid", "Partial"],
  ["unpaid", "Unpaid"],
  ["pending_verification", "Pending Verification"],
  ["outstanding", "Outstanding Items"],
];
const Field = ({ label, children }) => <label className="text-sm font-medium text-[#1B2C5A]">{label}{children}</label>;
const selectClassArm = (classOption) => classOption?.class_wide ? "all" : classOption?.arms?.[0]?.id || "";

export default function FinanceClassSummary() {
  const { user } = useAuth();
  const allowed = hasCapability(user, "finance.payment_status.view_class");
  const canViewStudent = hasCapability(user, "finance.payment_status.view_student");
  const canIssue = canIssuePhysicalItems(user);
  const [issuance, setIssuance] = useState(null);
  const [issuanceBusy, setIssuanceBusy] = useState(false);
  const [selectedCharges, setSelectedCharges] = useState([]);
  const [context, setContext] = useState({ sessions: [], terms: [], programs: [], classes: [] });
  const [filters, setFilters] = useState({ session_id: "", term_id: "", program_id: "", class_id: "", arm_id: "", item_id: "all", card_status: "all", issue_status: "all", q: "", page: 1 });
  const [data, setData] = useState(null), [student, setStudent] = useState(null), [error, setError] = useState(""), [loading, setLoading] = useState(false);
  const classes = useMemo(() => context.classes.filter((value) => value.program_id === filters.program_id), [context.classes, filters.program_id]);
  const selectedClass = context.classes.find((value) => value.id === filters.class_id);
  const armOptions = filters.class_id === "all"
    ? classes.flatMap((classOption) => (classOption.arms || []).map((arm) => ({ ...arm, label: classOption.name + " · " + arm.name })))
    : selectedClass?.arms || [];
  const operations = useMemo(() => flattenOperationalRows(data?.rows), [data?.rows]);
  const eligibleOperations = operations.filter((row) => row.item_type === "physical" && row.item_status === "awaiting_issue" && row.product_charge_id);

  const applyContext = useCallback((next, requestedSession = "") => {
    setContext(next);
    setFilters((current) => {
      const session = requestedSession || current.session_id || (next.sessions.find((value) => value.is_current) || next.sessions[0])?.id || "";
      const term = (next.terms.find((value) => value.id === current.term_id) || next.terms.find((value) => value.is_current) || next.terms[0])?.id || "";
      const program = next.programs.find((value) => value.id === current.program_id) || next.programs[0];
      const availableClasses = next.classes.filter((value) => value.program_id === program?.id);
      const keepAllClasses = current.class_id === "all" && availableClasses.length > 0;
      const classOption = availableClasses.find((value) => value.id === current.class_id) || availableClasses[0];
      return { ...current, session_id: session, term_id: term, program_id: program?.id || "", class_id: keepAllClasses ? "all" : classOption?.id || "", arm_id: keepAllClasses ? "all" : selectClassArm(classOption), item_id: "all", page: 1 };
    });
  }, []);

  const loadContext = useCallback(async (sessionId = "") => {
    try {
      const { data: next } = await api.get("/finance/payment-status/context", { params: sessionId ? { session_id: sessionId } : {} });
      applyContext(next, sessionId);
      setError("");
    } catch (requestError) { setError(formatApiError(requestError) || requestError?.message || "Request failed"); }
  }, [applyContext]);

  useEffect(() => { if (allowed) void loadContext(); }, [allowed, loadContext]);

  const loadSummary = useCallback(async () => {
    if (!filters.session_id || !filters.term_id || !filters.program_id || !filters.class_id || !filters.arm_id) return;
    setLoading(true);
    try {
      const { data: next } = await api.get("/finance/class-payment-summary", { params: { ...filters, arm_id: filters.arm_id === "all" ? undefined : filters.arm_id, item_id: filters.item_id === "all" ? undefined : filters.item_id, card_status: filters.card_status === "all" ? undefined : filters.card_status, issue_status: filters.issue_status === "all" ? undefined : filters.issue_status, page_size: 100 } });
      setData(next); setError("");
    } catch (requestError) { setError(formatApiError(requestError) || requestError?.message || "Request failed"); setData(null); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { const timer = setTimeout(() => { if (allowed) void loadSummary(); }, filters.q ? 250 : 0); return () => clearTimeout(timer); }, [allowed, loadSummary, filters.q]);
  useEffect(() => { setSelectedCharges([]); }, [filters.session_id, filters.term_id, filters.program_id, filters.class_id, filters.arm_id, filters.item_id, filters.card_status, filters.issue_status, filters.q, filters.page]);

  const selectProgram = (programId) => {
    const classOption = context.classes.find((value) => value.program_id === programId);
    setFilters({ ...filters, program_id: programId, class_id: classOption?.id || "", arm_id: selectClassArm(classOption), item_id: "all", page: 1 });
  };
  const selectClass = (classId) => {
    const classOption = context.classes.find((value) => value.id === classId);
    setFilters({ ...filters, class_id: classId, arm_id: classId === "all" ? "all" : selectClassArm(classOption), item_id: "all", page: 1 });
  };
  const openStudent = async (row) => {
    try {
      const { data: detail } = await api.get(`/finance/payment-status/students/${row.student_id}`, { params: { session_id: filters.session_id, term_id: filters.term_id, program_id: filters.program_id, class_id: row.class_id || filters.class_id, arm_id: row.arm_id || (filters.arm_id === "all" ? undefined : filters.arm_id), enrollment_id: row.enrollment_id } });
      setStudent(detail); setError("");
    } catch (requestError) { setError(formatApiError(requestError) || requestError?.message || "Request failed"); }
  };
  const prepareIssuance = async (item, action) => {
    try {
      setIssuanceBusy(true);
      const { data: inventory } = await api.get("/inventory");
      const stockItem = inventory.find((value) => value.id === item.inventory_item_id);
      if (!stockItem) throw new Error("Linked inventory stock is unavailable");
      setIssuance({ action, item, stockItem });
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError) || requestError?.message || "Request failed");
    } finally {
      setIssuanceBusy(false);
    }
  };

  const toggleSelected = (chargeId) => setSelectedCharges((current) =>
    current.includes(chargeId) ? current.filter((value) => value !== chargeId) : [...current, chargeId]);

  const prepareBulkIssuance = async () => {
    const items = eligibleOperations.filter((row) => selectedCharges.includes(row.product_charge_id));
    if (!items.length) return;
    try {
      setIssuanceBusy(true);
      const { data: inventory } = await api.get("/inventory");
      const grouped = items.reduce((result, item) => {
        const current = result[item.inventory_item_id] || { inventory_item_id: item.inventory_item_id, name: item.name, quantity: 0, unit: item.inventory_unit || "pcs" };
        current.quantity += Number(item.quantity || 1); result[item.inventory_item_id] = current; return result;
      }, {});
      const stockGroups = Object.values(grouped).map((group) => {
        const stockItem = inventory.find((value) => value.id === group.inventory_item_id);
        if (!stockItem) throw new Error(`Linked inventory stock is unavailable for ${group.name}`);
        if (Number(stockItem.quantity) < group.quantity) throw new Error(`Insufficient stock for ${group.name}: available ${stockItem.quantity}, requested ${group.quantity}`);
        return { ...group, currentStock: Number(stockItem.quantity), unit: stockItem.unit || group.unit };
      });
      setIssuance({ action: "bulk", items, stockGroups }); setError("");
    } catch (requestError) { setError(formatApiError(requestError) || requestError?.message || "Request failed"); }
    finally { setIssuanceBusy(false); }
  };

  const exportCurrentRows = async () => {
    try {
      const exported = []; let exportPage = 1; let total = 0;
      do {
        const { data: batch } = await api.get("/finance/class-payment-summary", { params: {
          ...filters, arm_id: filters.arm_id === "all" ? undefined : filters.arm_id,
          item_id: filters.item_id === "all" ? undefined : filters.item_id,
          card_status: filters.card_status === "all" ? undefined : filters.card_status,
          issue_status: filters.issue_status === "all" ? undefined : filters.issue_status,
          page: exportPage, page_size: 250,
        } });
        exported.push(...flattenOperationalRows(batch.rows)); total = batch.pagination.total; exportPage += 1;
      } while ((exportPage - 1) * 250 < total);
      const blob = new Blob([paymentOperationsCsv(exported)], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob); const link = document.createElement("a");
      link.href = url; link.download = "payment-status.csv"; link.click(); URL.revokeObjectURL(url);
    } catch (requestError) { setError(formatApiError(requestError) || requestError?.message || "Export failed"); }
  };

  const confirmIssuance = async () => {
    if (!issuance) return;
    try {
      setIssuanceBusy(true);
      if (issuance.action === "bulk") {
        await api.post("/finance/product-charges/bulk-issue", { charge_ids: issuance.items.map((item) => item.product_charge_id) });
        setSelectedCharges([]); setIssuance(null); await loadSummary();
        return;
      }
      if (!issuance.item?.product_charge_id || !student) return;
      const endpointAction = issuance.action === "issue" ? "issue" : "reverse";
      await api.post(`/finance/product-charges/${issuance.item.product_charge_id}/${endpointAction}`);
      setIssuance(null);
      await Promise.all([loadSummary(), openStudent(student)]);
    } catch (requestError) {
      setError(formatApiError(requestError) || requestError?.message || "Request failed");
    } finally {
      setIssuanceBusy(false);
    }
  };

  if (!allowed) return <div><PageHeader overline="Payments" title="Payment Status" description="Read-only class payment information."/><Card className="p-6 text-red-700">You do not have permission to view class payment status.</Card></div>;
  const summary = data?.summary || {};
  return <div><FinanceNav/><PageHeader overline="Payments" title="My Class Payment Status" description="Quick, read-only answers from DFA's central payment records."/>
    <Card className="mb-5 grid gap-3 p-4 md:grid-cols-3 lg:grid-cols-5">
      <Field label="Session"><select aria-label="Session" className="mt-1 w-full rounded-md border bg-white p-2.5" value={filters.session_id} onChange={(event) => void loadContext(event.target.value)}>{context.sessions.map((value) => <option key={value.id} value={value.id}>{value.name}</option>)}</select></Field>
      <Field label="Term"><select aria-label="Term" className="mt-1 w-full rounded-md border bg-white p-2.5" value={filters.term_id} onChange={(event) => setFilters({ ...filters, term_id: event.target.value, item_id: "all", page: 1 })}>{context.terms.map((value) => <option key={value.id} value={value.id}>{value.name}</option>)}</select></Field>
      <Field label="Programme"><select aria-label="Programme" className="mt-1 w-full rounded-md border bg-white p-2.5" value={filters.program_id} onChange={(event) => selectProgram(event.target.value)}>{context.programs.map((value) => <option key={value.id} value={value.id}>{value.name}</option>)}</select></Field>
      <Field label="Class"><select aria-label="Class" className="mt-1 w-full rounded-md border bg-white p-2.5" value={filters.class_id} onChange={(event) => selectClass(event.target.value)}><option value="all">All classes</option>{classes.map((value) => <option key={value.id} value={value.id}>{value.name}</option>)}</select></Field>
      <Field label="Arm"><select aria-label="Arm" className="mt-1 w-full rounded-md border bg-white p-2.5" value={filters.arm_id} onChange={(event) => setFilters({ ...filters, arm_id: event.target.value, page: 1 })}>{(filters.class_id === "all" || selectedClass?.class_wide) && <option value="all">All arms</option>}{armOptions.map((value) => <option key={value.id} value={value.id}>{value.label || value.name}</option>)}</select></Field>
      <Field label="Payment item"><select aria-label="Payment item" className="mt-1 w-full rounded-md border bg-white p-2.5" value={filters.item_id} onChange={(event) => setFilters({ ...filters, item_id: event.target.value, page: 1 })}><option value="all">All configured items</option>{data?.available_items?.map((value) => <option key={value.item_id} value={value.item_id}>{value.name}</option>)}</select></Field>
      <Field label="Issue status"><select aria-label="Issue status" className="mt-1 w-full rounded-md border bg-white p-2.5" value={filters.issue_status} onChange={(event) => setFilters({ ...filters, issue_status: event.target.value, page: 1 })}>{ISSUE_FILTERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
      <Field label="Student search"><Input aria-label="Student search" className="mt-1" placeholder="Name, admission number or class" value={filters.q} onChange={(event) => setFilters({ ...filters, q: event.target.value, page: 1 })}/></Field>
    </Card>
    {error && <Card className="mb-5 border-red-200 p-4 text-red-700" role="alert">{error}</Card>}
    <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">{STATUS_FILTERS.map(([status, label]) => <button key={status} type="button" onClick={() => setFilters({ ...filters, card_status: status, page: 1 })} className={`rounded-xl border p-3 text-left ${filters.card_status === status ? "border-[#1B2C5A] bg-[#1B2C5A] text-white" : "bg-white text-[#1B2C5A]"}`}><span className="block text-xs">{label}</span><strong className="text-xl">{status === "all" ? summary.total_items || 0 : status === "outstanding" ? summary.outstanding || 0 : summary[status] || 0}</strong></button>)}</div>
    <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">{ISSUE_FILTERS.slice(1).map(([status, label]) => <button key={status} type="button" onClick={() => setFilters({ ...filters, card_status: status, page: 1 })} className={`rounded-xl border p-3 text-left ${filters.card_status === status ? "border-[#6B7A28] bg-[#6B7A28] text-white" : "bg-white text-[#1B2C5A]"}`}><span className="block text-xs">{label}</span><strong className="text-xl">{summary[status] || 0}</strong></button>)}</div>
    <Card className="mb-5 flex flex-wrap items-center justify-between gap-3 p-4"><span><strong>{summary.total_students || 0}</strong> students · <strong>{summary.applicable_students || 0}</strong> with configured charges</span><span className="text-lg font-semibold text-red-700">Outstanding: {paymentMoney(summary.outstanding_amount_minor)}</span></Card>
    {loading && <Card className="p-8 text-center text-slate-500">Loading authorized payment status…</Card>}
    {!loading && data && <>
      <Card className="mb-3 flex flex-wrap items-center justify-between gap-3 p-4 print:hidden">
        <div className="flex flex-wrap gap-2">
          {canIssue && <Button disabled={!selectedCharges.length || issuanceBusy} onClick={() => void prepareBulkIssuance()}>Issue Selected ({selectedCharges.length})</Button>}
          <Button variant="outline" disabled={!operations.length} onClick={() => void exportCurrentRows()}>Export CSV</Button>
          <Button variant="outline" disabled={!operations.length} onClick={() => window.print()}>Print</Button>
        </div>
        <span className="text-sm text-slate-500">{operations.length} item-level records on this page</span>
      </Card>
      <Card className="overflow-x-auto" data-testid="payment-operations-list"><Table><TableHeader><TableRow>
        {canIssue && <TableHead className="print:hidden">Select</TableHead>}<TableHead>Student</TableHead><TableHead>Admission Number</TableHead><TableHead>Class</TableHead><TableHead>Arm</TableHead>
        <TableHead>Item / Service</TableHead><TableHead>Type</TableHead><TableHead className="text-center">Qty</TableHead><TableHead className="text-right">Charge</TableHead>
        <TableHead className="text-right">Scholarship</TableHead><TableHead className="text-right">Discount</TableHead><TableHead className="text-right">Cash Paid</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Payment Status</TableHead><TableHead>Issue Status</TableHead><TableHead className="print:hidden">Action</TableHead>
      </TableRow></TableHeader><TableBody>{operations.map((row) => {
        const eligible = row.item_type === "physical" && row.item_status === "awaiting_issue" && row.product_charge_id;
        return <TableRow key={`${row.enrollment_id}:${row.invoice_id}`}>
          {canIssue && <TableCell className="print:hidden">{eligible && <input aria-label={`Select ${row.student_name} ${row.name}`} type="checkbox" checked={selectedCharges.includes(row.product_charge_id)} onChange={() => toggleSelected(row.product_charge_id)}/>}</TableCell>}
          <TableCell className="font-medium">{row.student_name}</TableCell><TableCell>{row.admission_no}</TableCell><TableCell>{row.class_name}</TableCell><TableCell>{row.arm_name || "—"}</TableCell>
          <TableCell>{row.name}</TableCell><TableCell>{row.item_type === "physical" ? "Physical Item" : "Service"}</TableCell><TableCell className="text-center">{row.quantity || 1}</TableCell>
          <TableCell className="text-right">{paymentMoney(row.required_minor)}</TableCell><TableCell className="text-right">{paymentMoney(row.scholarship_minor)}</TableCell><TableCell className="text-right">{paymentMoney(row.discount_minor)}</TableCell><TableCell className="text-right">{paymentMoney(row.paid_minor)}</TableCell><TableCell className="text-right font-semibold">{paymentMoney(row.outstanding_minor)}</TableCell>
          <TableCell>{paymentStatusLabel(row.status)}</TableCell><TableCell>{row.item_type === "physical" ? paymentStatusLabel(row.item_status) : "Not Applicable"}</TableCell>
          <TableCell className="print:hidden">{canViewStudent && <Button size="sm" variant="outline" onClick={() => void openStudent(row)}>View summary</Button>}</TableCell>
        </TableRow>;
      })}</TableBody></Table>{!operations.length && <p className="p-8 text-center text-slate-500">No charged items match this authorized view.</p>}</Card>
    </>}
    {data?.pagination?.total > data?.pagination?.page_size && <div className="mt-4 flex justify-end gap-2"><Button variant="outline" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>Previous</Button><Button variant="outline" disabled={filters.page * 100 >= data.pagination.total} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>Next</Button></div>}
    {student && <Card className="mt-5 p-5" data-testid="student-payment-summary">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[#6B7A28]">Payment Summary</p>
          <h2 className="text-xl font-semibold text-[#1B2C5A]">{student.student_name}</h2>
          <p className="text-sm text-slate-500">{student.admission_no} · {[student.class_name, student.arm_name].filter(Boolean).join(" · ")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setStudent(null)}>Close</Button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item / Service</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-center">Qty</TableHead>
              <TableHead className="text-right">Charge</TableHead>
              <TableHead>Payment Status</TableHead>
              <TableHead>Issue Status</TableHead>
              <TableHead className="text-right">Scholarship</TableHead><TableHead className="text-right">Discount</TableHead><TableHead className="text-right">Cash Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              {canIssue && <TableHead>Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {student.items.map((value) => (
              <TableRow key={value.invoice_id}>
                <TableCell className="font-medium">{value.name}</TableCell>
                <TableCell>{value.item_type === "physical" ? "Physical Item" : "Service"}</TableCell>
                <TableCell className="text-center">{value.quantity || 1}</TableCell>
                <TableCell className="text-right">{paymentMoney(value.required_minor)}</TableCell>
                <TableCell>{paymentStatusLabel(value.status)}</TableCell>
                <TableCell>
                  {value.item_type === "physical"
                    ? paymentStatusLabel(value.item_status)
                    : "Not Applicable"}
                </TableCell>
                <TableCell className="text-right">{paymentMoney(value.scholarship_minor)}</TableCell><TableCell className="text-right">{paymentMoney(value.discount_minor)}</TableCell><TableCell className="text-right">{paymentMoney(value.paid_minor)}</TableCell>
                <TableCell className="text-right font-semibold">{paymentMoney(value.outstanding_minor)}</TableCell>
                {canIssue && <TableCell>
                  {value.item_type === "physical" && value.item_status === "awaiting_issue" && (
                    <Button size="sm" disabled={issuanceBusy} onClick={() => void prepareIssuance(value, "issue")}>Issue Item</Button>
                  )}
                  {value.item_type === "physical" && value.item_status === "issued" && (
                    <Button size="sm" variant="outline" disabled={issuanceBusy} onClick={() => void prepareIssuance(value, "reverse")}>Reverse Issue</Button>
                  )}
                </TableCell>}



              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>}
    <Dialog open={Boolean(issuance)} onOpenChange={(open) => !open && !issuanceBusy && setIssuance(null)}>
      <DialogContent data-testid="issuance-confirmation">
        <DialogHeader>
          <DialogTitle>{issuance?.action === "bulk" ? "Issue selected items?" : issuance?.action === "issue" ? "Issue physical item?" : "Reverse issued item?"}</DialogTitle>
          <DialogDescription>
            Confirm the inventory movement. The backend remains authoritative for payment, stock and repeat-action checks.
          </DialogDescription>
        </DialogHeader>
        {issuance?.action === "bulk" ? <div className="space-y-3 text-sm">
          <p><strong>Students:</strong> {new Set(issuance.items.map((item) => item.student_id)).size}</p>
          <p><strong>Total units:</strong> {issuance.items.reduce((total, item) => total + Number(item.quantity || 1), 0)}</p>
          {issuance.stockGroups.map((group) => <div key={group.inventory_item_id} className="rounded-md border p-3">
            <p className="font-semibold">{group.name}: {group.quantity} units</p>
            <p>Current stock: {group.currentStock} {group.unit}</p><p>Stock after issue: {group.currentStock - group.quantity} {group.unit}</p>
          </div>)}
        </div> : issuance && <div className="space-y-2 text-sm">
          <p><strong>Student:</strong> {student?.student_name}</p><p><strong>Item:</strong> {issuance.item.name}</p><p><strong>Quantity:</strong> {issuance.item.quantity || 1}</p>
          <p><strong>Current stock:</strong> {issuance.stockItem.quantity} {issuance.stockItem.unit || issuance.item.inventory_unit || "pcs"}</p>
          <p><strong>Stock after {issuance.action}:</strong> {Number(issuance.stockItem.quantity) + (issuance.action === "issue" ? -Number(issuance.item.quantity || 1) : Number(issuance.item.quantity || 1))} {issuance.stockItem.unit || issuance.item.inventory_unit || "pcs"}</p>
        </div>}
        <DialogFooter>
          <Button variant="outline" disabled={issuanceBusy} onClick={() => setIssuance(null)}>Cancel</Button>
          <Button disabled={issuanceBusy} onClick={() => void confirmIssuance()}>
            {issuanceBusy ? "Saving…" : issuance?.action === "bulk" ? "Confirm Issue Selected" : issuance?.action === "issue" ? "Confirm Issue" : "Confirm Reversal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>;
}
